import React, { useEffect, useMemo, useState } from 'react'
import {
  ColumnSort,
  PaginationState,
  Table as TableApi
} from '@tanstack/react-table'
import { AgentLanguageEnum } from '../../../../graphql/schema/queries/agent-features'
import { transformRole } from '../../../../graphql/schema/queries/helpers/role'
import { AgentFeature } from '../../../../components/agent-features'
import { Application } from '../../../../components/application/api/requests/get-application'
import { useApplication } from '../../../../components/application/api/use-application'
import { useOrganizationFeature } from '../../../../components/organization-feature'
import { useOrganizationProfile } from '../../../../components/session-metadata/api'
import { FilteredActionList } from '../actions/action-list'
import { LibraryGridPanelID } from '../../helpers/drilldown-ui-interfaces'
import { LibraryDrillDownPanel } from '../drilldown-panel'
import { clearPanelParameters } from '../../helpers/cvssUtils'
import { Library } from '../../helpers/ui-interfaces'
import { useFilters as useScaFilters } from './useFilters'
import {
  LibraryFilterType,
  LibraryAssessGrid
} from '../../helpers/libraries-interfaces'
import {
  assesLibraryFilters,
  getGridLibrary,
  LibraryFilterQueryFormData,
  useAgentFeatures,
  useLibraries
} from '../../api/react-query'
import { getColumns } from './columns'
import {
  getMappedLibrary,
  mapExportLibraries
} from '../../helpers/object-transforms'
import { useLibrariesPageContext } from '../../pages'
import { FilterLibrariesActionType } from '../actions/bulk-delete-modal'
import { Organization, Role } from '@contrast-security-inc/base-types'
import { useCurrentOrganization } from 'contrastBase/state'
import { useFilterDefinitions } from '../filters/use-filter-definitions'
import { Layout } from '../filters/layout'
import {
  SkeletorTableState,
  Table
} from '@contrast-security-inc/design-system-tables'
import { css } from '@emotion/css'
import { useDeepCompareEffect } from 'use-deep-compare'
import { hasRole } from 'contrastBase/RoleSwitch'
import { QuickFilter } from '../filters/types'

export const ASSESS_GRID_TEST_ID = 'libraries-assess-grid'

interface AssessGridProps {
  organizationID: string
}

interface LibrariesGridProps extends AssessGridProps {
  appLibraryStatusEnabled: boolean
  applicationRole: Role | undefined
  organizationRole: Role
  organization: Organization
  application?: Application
}

export const AssessGrid = ({ organizationID }: AssessGridProps) => {
  const { currentOrganization: organization } = useCurrentOrganization()

  const { applicationID, gridType } = useLibrariesPageContext()

  const { data: application, isLoading: isLoadingApp } = useApplication(
    organizationID,
    applicationID ? applicationID : '',
    applicationID !== undefined && gridType === 'Application'
  )

  const { data: organizationProfile, isLoading: isLoadingOrgProfile } =
    useOrganizationProfile({
      organizationUuid: organizationID
    })

  const applicationRole = useMemo(
    () => transformRole(application?.roles),
    [application]
  )
  const organizationRole = useMemo(
    () =>
      transformRole([
        ...(organizationProfile?.legacyRoles || []),
        ...(organizationProfile?.roles || [])
      ]),
    [organizationProfile]
  )

  const { enabled: appLibraryStatusEnabled, isLoading: isLoadingOrgFeature } =
    useOrganizationFeature(organizationID, 'app-library-status')

  if (
    gridType === 'Application' &&
    !isLoadingOrgProfile &&
    !isLoadingApp &&
    !application &&
    !isLoadingOrgFeature &&
    appLibraryStatusEnabled !== undefined
  ) {
    throw new Error('Unable to load the application')
  }

  return organization && organizationRole ? (
    <AssessLibrariesGrid
      appLibraryStatusEnabled={appLibraryStatusEnabled}
      applicationRole={applicationRole}
      organizationRole={organizationRole}
      organizationID={organizationID}
      application={application}
      organization={organization}
    />
  ) : null
}

export const AssessLibrariesGrid = ({
  organizationID,
  organization,
  application,
  applicationRole,
  organizationRole,
  appLibraryStatusEnabled
}: LibrariesGridProps) => {
  const tableRef = React.useRef<TableApi<LibraryAssessGrid> | null>(null)
  const {
    applicationID,
    serverID,
    gridType,
    filterText,
    activeRow,
    showPanel,
    selectedQuickFilter
  } = useLibrariesPageContext()
  const [isPanelOpen, setIsPanelOpen] = useState(!!showPanel)
  // @ts-ignore
  const isAllSelected = tableRef.current?.getState().isAllSelected
  // @ts-ignore
  const excludedRows = tableRef.current?.getState().excludedRows
  const [selectedPanelLibrary, setSelectedPanelLibrary] = useState<Library>()
  const [selectedPanelTab, setSelectedPanelTab] = useState<LibraryGridPanelID>(
    LibraryGridPanelID.Overview
  )
  const apps = useMemo(
    () => (applicationID ? [applicationID] : []),
    [applicationID]
  )

  const hasAppEditPermission = applicationRole
    ? hasRole(applicationRole, 'EDIT')
    : false

  const servers = useMemo(() => (serverID ? [serverID] : []), [serverID])

  const excludeFilters = [
    ...(gridType !== 'Application' || !applicationID || !appLibraryStatusEnabled
      ? [LibraryFilterType.Status]
      : []),
    ...(gridType === 'Application' ? [LibraryFilterType.Apps] : []),
    ...(gridType === 'Server' ? [LibraryFilterType.Servers] : [])
  ]

  const [selectedAggregationFilters, setSelectedAggregationFilters] =
    useState<Record<string, any> | null>(null)

  const formData: LibraryFilterQueryFormData = useMemo(() => {
    const updatedFilters = {}

    let includeUnused = false
    let includeUsed = false

    Object.keys(selectedAggregationFilters || []).forEach((key: string) => {
      if (key === LibraryFilterType.Usage) {
        selectedAggregationFilters?.[LibraryFilterType.Usage].forEach(
          (usageFilter: string) => {
            if (usageFilter.endsWith('.used')) {
              includeUsed = true
            }

            if (usageFilter.endsWith('.unused')) {
              includeUnused = true
            }
          }
        )
      } else {
        updatedFilters[key.toLowerCase()] = selectedAggregationFilters?.[
          key
        ].map((filter) => {
          // Remove the prepended tag name that comes from the filter
          return filter.replace(`${key}.`, '')
        })
      }
    })

    return {
      q: filterText || '',
      quickFilter: selectedQuickFilter,
      apps,
      servers,
      environments: [],
      grades: [],
      languages: [],
      licenses: [],
      status: [],
      severities: [],
      tags: [],
      ...updatedFilters,
      includeUnused,
      includeUsed
    }
  }, [
    selectedAggregationFilters,
    filterText,
    selectedQuickFilter,
    apps,
    servers
  ])

  const [sortingOrder, setSortingOrder] = useState<ColumnSort[]>([
    {
      id: 'grade',
      desc: false
    }
  ])

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  })

  const { pageIndex, pageSize } = pagination

  useEffect(() => {
    ;(async () => {
      if (showPanel && activeRow) {
        await setPanelLibrary({ filterText })
        setIsPanelOpen(true)
        const url = decodeURIComponent(window.location.href)
        const updatedUrl = clearPanelParameters(url)

        window.history.replaceState(null, 'null', updatedUrl)
      }
    })()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPanel, activeRow])

  useEffect(() => {
    // Ensure Overview panel is always default, unless the usage column is clicked
    if (!isPanelOpen) {
      setSelectedPanelTab(LibraryGridPanelID.Overview)
    }
  }, [isPanelOpen])

  const { results, libraryFilters } = useScaFilters({
    organizationID,
    formData
  })

  /** Unchecks all selected rows and clears selected row state. */
  const resetRowSelection = () => {
    tableRef.current?.resetRowSelection()
    ;(tableRef.current?.getState() as SkeletorTableState)?.resetSelectedRows?.()
  }

  const {
    data: libraryData,
    refetch: refetchLibraries,
    isLoading: isLoadingLibraries,
    isFetched: hasFetchedLibraries,
    isPreviousData
  } = useLibraries({
    formData,
    keepPreviousData: true,
    organizationID,
    pageIndex,
    pageSize,
    sortingOrder,
    enabled: true,
    onSettled: resetRowSelection
  })

  const totalResults = libraryData?.count
  const pageCount = Math.ceil(
    totalResults && pageSize ? totalResults / pageSize : 0
  )

  const selectedQuickFilterMap = React.useMemo(
    () =>
      libraryData?.quickFilters?.find(
        ({ filterType }) => filterType === selectedQuickFilter
      ),
    [libraryData?.quickFilters, selectedQuickFilter]
  )

  const quickFilters: QuickFilter[] = useMemo(() => {
    return libraryData && libraryData.quickFilters
      ? libraryData.quickFilters.map((filter) => {
          return {
            name: filter.name,
            id: filter.filterType,
            count: filter.count
          }
        })
      : []
  }, [libraryData])

  const { data: agentFeatures } = useAgentFeatures(!hasFetchedLibraries)

  const features = React.useMemo(
    () =>
      agentFeatures && Array.isArray(agentFeatures.features)
        ? agentFeatures.features.map(({ name, languages }): AgentFeature => {
            return {
              name,
              languages: languages.map((lang) => AgentLanguageEnum[lang])
            }
          })
        : [],
    [agentFeatures]
  )

  const setPanelLibrary = async ({
    library,
    filterText
  }: {
    library?: LibraryAssessGrid
    filterText?: string
  }) => {
    let selectedLibrary = library

    if (!library && filterText) {
      selectedLibrary = await getGridLibrary(organizationID, {
        quickFilter: 'ALL',
        apps,
        servers,
        tags: [],
        languages: [],
        licenses: [],
        grades: [],
        environments: [],
        severities: [],
        status: [],
        includeUsed: false,
        includeUnused: false,
        q: filterText
      })
    }

    if (selectedLibrary) {
      setIsPanelOpen(true)
      setSelectedPanelLibrary(
        await getMappedLibrary(organizationID, selectedLibrary)
      )
    } else {
      setIsPanelOpen(false)
      setSelectedPanelLibrary(undefined)
    }
  }

  const refetchData = async () => {
    await refetchLibraries({ stale: true, queryKey: 'data-refetch' })

    results.forEach(async (query) => {
      await query.refetch()
    })

    resetRowSelection()
  }

  const columns = useMemo(() => {
    return features
      ? getColumns({
          features,
          organization,
          application,
          applicationID,
          setPanelLibrary,
          setSelectedPanelTab,
          applicationRole,
          organizationRole,
          refresh: refetchData,
          gridType,
          appLibraryStatusEnabled,
          serverID
        })
      : []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationRole, features, organizationRole, appLibraryStatusEnabled])

  const getFilterActionListFilters = () => {
    const filters = {
      applications: gridType === 'Organization' ? formData.apps : [],
      grades: formData.grades,
      tags: formData.tags,
      languages: formData.languages,
      licenses: formData.licenses,
      environments: formData.environments,
      status: formData.status,
      usage: [
        ...(formData.includeUsed ? ['used'] : []),
        ...(formData.includeUnused ? ['unused'] : [])
      ]
    }

    for (const filterKey of Object.keys(filters)) {
      if (filters[filterKey].length === 0) {
        delete filters[filterKey]
      }
    }

    return filters
  }

  const selectedCount =
    isAllSelected && totalResults
      ? totalResults - (excludedRows?.length || 0)
      : tableRef.current?.getSelectedRowModel().rows.length || 0

  const { filterState } = useFilterDefinitions({
    libraryFilters,
    excludeFilters,
    results,
    initialFilters: assesLibraryFilters
  })

  useDeepCompareEffect(() => {
    // Reset page index when filtering
    setPagination((prevState) => ({
      ...prevState,
      pageIndex: 0
    }))
  }, [selectedQuickFilter, filterState.filters])

  React.useEffect(() => {
    setSelectedAggregationFilters(filterState.filters)
  }, [filterState.filters, libraryFilters])

  return (
    <Layout
      filterState={filterState}
      quickFilters={quickFilters}
      selectedQuickFilterMap={selectedQuickFilterMap}
    >
      <Table
        className={css`
          padding: 0 24px;

          /* Removes space on top of the header showing when scrolling. */
          thead > tr {
            top: -1px;
          }
          /* Center aligns checkbox header */
          thead > tr > th:first-child > div > div {
            width: 100%;
          }
        `}
        columns={columns}
        data={
          libraryData?.libraries && libraryData.libraries.length > 0
            ? libraryData.libraries
            : []
        }
        isLoading={
          (!!columns?.length && isLoadingLibraries) ||
          (!!totalResults && isPreviousData)
        }
        PaginationProps={{
          totalResults,
          showInputNav: true,
          allowFirstPageNav: true
        }}
        testId={ASSESS_GRID_TEST_ID}
        tableOptions={{
          enableSorting: true,
          manualPagination: true,
          getRowId: (row) => row.hash,
          onSortingChange: (updaterOrValue) => setSortingOrder(updaterOrValue),
          onPaginationChange: (getPagination: () => PaginationState) => {
            const { pageIndex, pageSize } = getPagination() || {}
            if (pageSize) {
              setPagination({
                pageIndex,
                pageSize
              })
            }
          },
          enableRowSelection: true,
          enableMultiRowSelection: true,
          pageCount,
          state: { pagination: { pageIndex, pageSize }, sorting: sortingOrder },
          initialState: { sorting: sortingOrder }
        }}
        BulkSelectionProps={{
          totalCount: totalResults,
          renderBulkSelectionItems: () => [
            <FilteredActionList
              hasAppEditPermission={hasAppEditPermission}
              key="assess-bulk-selection-bar"
              organizationID={organizationID}
              organizationRole={organizationRole}
              showStatusAction={
                gridType === 'Application' && appLibraryStatusEnabled
              }
              libraries={
                tableRef.current?.getIsAllPageRowsSelected() ||
                selectedCount > pageSize
                  ? []
                  : mapExportLibraries(
                      tableRef.current?.getSelectedRowModel().rows || []
                    )
              }
              operationType={
                tableRef.current?.getIsAllPageRowsSelected() ||
                selectedCount > pageSize
                  ? FilterLibrariesActionType.BY_FILTERS
                  : FilterLibrariesActionType.BY_HASHES
              }
              filters={{
                quickFilter: formData.quickFilter,
                filterText: filterText || '',
                aggregations: getFilterActionListFilters()
              }}
              excludedLibraries={excludedRows || []}
              refresh={refetchData}
              applicationID={applicationID}
              serverID={serverID}
            />
          ]
        }}
        getTableApi={(api) => {
          tableRef.current = api as TableApi<LibraryAssessGrid>
        }}
      />
      {isPanelOpen && organization && selectedPanelLibrary && (
        <LibraryDrillDownPanel
          ossEnabled={organization.ossLicense}
          organizationID={organizationID}
          onClose={() => {
            setIsPanelOpen(false)
            setSelectedPanelLibrary(undefined)
          }}
          selectedPanelTabId={selectedPanelTab}
          applicationID={applicationID}
          gridType={gridType}
          organization={organization}
          library={selectedPanelLibrary}
          organizationRole={organizationRole}
          refresh={refetchData}
        />
      )}
    </Layout>
  )
}
